import { Box, Divider, Tooltip, useTheme } from '@mui/material';
import Copy from 'components/Copy';
import { IntegrationStateIndicator } from 'IntegrationModule/components/IntegrationStateIndicator/IntegrationStateIndicator';
import { integrationsHash } from '@constants/integration';
import { IntegrationState } from 'types/integration';
import { TeamSearchIntegration } from 'TeamModule/api/searchTeamsByFieldsApi';
import { getAiSuggestionsState } from 'TeamModule/utils';
import { DataCellProps } from '../types';

const Item = ({ label, value }: { label: string; value?: string | number | boolean | null }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        mb: 1,
        mr: 4,
        minWidth: 220,
      }}
    >
      <Box
        sx={{
          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6b7280',
          fontSize: '0.8rem',
          mb: 0.5,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontSize: '0.95rem',
          color: theme.palette.text.primary,
        }}
      >
        {value ?? ''}
      </Box>
    </Box>
  );
};

const Bool = ({ label, value }: { label: string; value?: boolean }) => (
  <Item label={label} value={value ? 'Yes' : 'No'} />
);

const Uuid = ({ uuid }: { uuid?: string }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        mb: 1,
        mr: 4,
        minWidth: 400,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box>
        <Box
          sx={{
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6b7280',
            fontSize: '0.8rem',
            mb: 0.5,
          }}
        >
          UUID
        </Box>
        <Box
          style={{ wordBreak: 'break-all' }}
          sx={{
            color: theme.palette.text.primary,
          }}
        >
          {uuid ?? ''}
        </Box>
      </Box>
      {uuid ? (
        <Box
          sx={{
            ml: 1,
          }}
        >
          <Tooltip title="Copy UUID">
            <span>
              <Copy textToCopy={uuid} />
            </span>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );
};

const TeamsV2DetailsRowCell = ({ team }: DataCellProps) => {
  const {
    id,
    internalTestTeam,
    ddiEnabled,
    openApiEnabled,
    ssoEnabled,
    chelloCompliant,
    aiEnabled,
    aiBlocked,
    reasonToArchive,
    lastBackfilled,
    archivedAt,
    scheduledArchiveDate,
    csm,
    integrations,
  } = team;

  const theme = useTheme();

  const integrationObjects = (integrations || []).filter(
    (i): i is TeamSearchIntegration => typeof i !== 'string',
  );

  return (
    <Box
      sx={{
        p: 2,
        bgcolor:
          theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.4)' : 'rgba(248, 250, 252, 0.9)',
        borderTop:
          theme.palette.mode === 'dark'
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(226, 232, 240, 0.8)',
        textAlign: 'left',
      }}
    >
      {/* Top quick info row */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Uuid uuid={id} />
      </Box>

      <Divider style={{ margin: '8px 0 12px' }} />

      {/* Grid of details */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Bool label="Is Test Team" value={internalTestTeam} />
        <Bool label="DDI Enabled" value={ddiEnabled} />
        <Bool label="SSO Enabled" value={ssoEnabled} />
        <Bool label="Open API Enabled" value={openApiEnabled} />
        <Bool label="Chello Compliant" value={chelloCompliant} />
        <Item label="AI Suggestions" value={getAiSuggestionsState(aiEnabled, aiBlocked)} />
        <Item label="CSM" value={csm || ''} />
        <Item label="Archived At" value={archivedAt || ''} />
        <Item label="Last Backfilled" value={lastBackfilled || ''} />
        <Item label="Reason to Archive" value={reasonToArchive || ''} />
      </Box>

      {/* Integration details */}
      {integrationObjects.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Box
            sx={{
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6b7280',
              fontSize: '0.8rem',
              mb: 1,
            }}
          >
            Integrations
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {integrationObjects.map((integration) => (
              <Box
                key={`${integration.targetServiceId}-${integration.state}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <IntegrationStateIndicator
                  state={integration.state as IntegrationState}
                  deletionInProgress={integration.deletionInProgress}
                />
                <Box
                  sx={{
                    fontSize: '0.95rem',
                    color: theme.palette.text.primary,
                  }}
                >
                  {integrationsHash[integration.targetService]?.label || integration.targetService}
                </Box>
                {integration.archivedAt && (
                  <Box
                    sx={{
                      fontSize: '0.8rem',
                      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#9ca3af',
                    }}
                  >
                    (Archived: {integration.archivedAt})
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TeamsV2DetailsRowCell;
